// What a child ticket starts out as: everything editable, copied from the parent.
//
// A child is "the same call, a different issue" (docs/API.md §3.3 `parent_ticket_id`),
// so the agent spawning one from the parent's screen wants the same caller, the same
// queue, the same form and the same tags — and then to change the one thing that
// differs. Prefilling everything makes that one edit; prefilling nothing makes it a
// retyped ticket. This maps the §3.2 detail shape the parent arrived in onto the three
// pieces of state web/src/pages/tickets/New.jsx keeps, and it is PURE so the mapping can
// be pinned in a test without a browser: the detail nests `queue.id` where the form
// wants `queue_id`, and a mapping that read the wrong one would come back `undefined`
// and quietly fall back to the default queue — the exact mistake `findByPublicRef`
// once made on the server.
//
// Two things are deliberately NOT copied:
//
//   * Status. A sub-issue starts `new` whatever the parent has reached — a child
//     spawned from a solved parent is not solved.
//   * The assignee. Agent-created tickets belong to the agent who created them
//     (docs/API.md §3.3), and the person clicking (+) is that agent, not whoever owns
//     the parent. `assigneeFor` applies the same default the form uses for any new
//     ticket, so a child and a fresh ticket agree.

/** The form's default owner: the current user, if they may own tickets at all. */
export function assigneeFor(user) {
  return user?.can_be_assigned && user?.id ? String(user.id) : '';
}

/**
 * A `custom_fields` value from the detail (typed JSON, docs/API.md §1.9) → what the
 * form's inputs hold. Checkboxes and multiselects keep their type; everything else is
 * text, because that is what an `<input>` holds and what `encodeCustomFields` parses
 * back on the way out.
 */
function formValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean' || Array.isArray(value)) return value;
  return String(value);
}

/**
 * @param {object} parent  The §3.2 detail shape of the parent ticket.
 * @param {{id?: string, can_be_assigned?: boolean}} user  The agent spawning the child.
 * @returns {{caller: object, fields: object, composer: object}}  New.jsx's three
 *   state slices, complete — a caller that spreads these over the empties gets every
 *   key.
 */
export function prefillFromParent(parent, user) {
  const customFields = {};
  for (const [key, value] of Object.entries(parent.custom_fields ?? {})) {
    customFields[key] = formValue(value);
  }

  return {
    caller: {
      // The same three shapes CallerPanel keeps in a draft (its `organizationShape`
      // and the location and customer rows it stores as-is).
      organization: parent.organization
        ? {
            id: parent.organization.id,
            name: parent.organization.name,
            domains: parent.organization.domains ?? [],
            location_count: null,
          }
        : null,
      location: parent.location
        ? {
            id: parent.location.id,
            organization_id: parent.location.organization_id ?? parent.organization?.id ?? null,
            name: parent.location.name,
            account_number: parent.location.account_number ?? null,
            address: parent.location.address ?? null,
            phone: parent.location.phone ?? null,
            timezone: parent.location.timezone ?? null,
          }
        : null,
      customer: parent.customer
        ? {
            id: parent.customer.id,
            name: parent.customer.name,
            email: parent.customer.email ?? null,
            phone: parent.customer.phone ?? null,
            position: parent.customer.position ?? null,
            organization_id: parent.customer.organization_id ?? null,
            location_id: parent.customer.location_id ?? null,
          }
        : null,
    },
    fields: {
      channel: parent.channel ?? 'phone',
      priority: parent.priority ?? 'normal',
      status: 'new',
      status_id: null,
      queue_id: parent.queue?.id ?? '',
      assigned_user_id: assigneeFor(user),
      form_id: parent.form?.id ?? '',
      device_serial: parent.device_serial ?? '',
      tags: [...(parent.tags ?? [])],
      follower_user_ids: (parent.followers ?? []).map((follower) => follower.user.id),
      cc_customer_ids: (parent.ccs ?? []).map((cc) => cc.customer.id),
      custom_fields: customFields,
      links: [],
    },
    composer: {
      // The parent's subject, to be edited rather than retyped: "Store 14 cannot take
      // payments" becomes "Store 14 cannot take payments — receipt printer" in one
      // keystroke sequence, and the two read as siblings in the queue.
      subject: parent.subject ?? '',
      body_text: '',
      is_internal_note: true,
    },
  };
}
